import { useRef, useState, useEffect } from 'react';
import { useScreenshot } from 'use-react-screenshot';
import useSentryLogger from '../../../hooks/useSentryLogger';
import logger from './../../../utils/logger.js';


const useScreenshotCapture = (screenshotRef, job_interview_id, BASE_URL) => {
  // Get Sentry logger
  const {
    logScreenshotEvent,
    logScreenshotError
  } = useSentryLogger();

  const [interviewStartTime, setInterviewStartTime] = useState(null);
  const screenshotBufferRef = useRef([]);  // Buffer to store screenshots
  const lastScreenshotTimeRef = useRef(null); // Track last screenshot time
  const isSendingRef = useRef(false); // Flag to prevent multiple simultaneous sends
  
  const [image, takeScreenshot] = useScreenshot({
    type: 'image/png',
    quality: 1.0
  });

  // Initialize start time when hook is loaded
  useEffect(() => {
    // Try to get stored start time from localStorage
    const storedStartTime = localStorage.getItem(`interview_start_time_${job_interview_id}`);
    const storedLastScreenshot = localStorage.getItem(`last_screenshot_${job_interview_id}`);
    
    if (storedStartTime) {
      setInterviewStartTime(new Date(storedStartTime));
      if (storedLastScreenshot) {
        const lastScreenshot = JSON.parse(storedLastScreenshot);
        lastScreenshotTimeRef.current = new Date(lastScreenshot.timestamp);
        screenshotBufferRef.current = [lastScreenshot];
        logScreenshotEvent('restored from storage', {
          interview_id: job_interview_id,
          hasStoredScreenshot: true
        });
      }
    } else {
      const newStartTime = new Date();
      setInterviewStartTime(newStartTime);
      localStorage.setItem(`interview_start_time_${job_interview_id}`, newStartTime.toISOString());
      logScreenshotEvent('initialized', {
        interview_id: job_interview_id,
        startTime: newStartTime.toISOString()
      });
    }

    // Add event listener for beforeunload
    const handleBeforeUnload = async (e) => {
      if (screenshotBufferRef.current.length > 0) {
        e.preventDefault();
        e.returnValue = '';
        await sendScreenshotsToServer();
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    // Add event listener for visibility change
    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'hidden' && screenshotBufferRef.current.length > 0) {
        await sendScreenshotsToServer();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [job_interview_id]);

  // Function to capture screenshot with timestamp
  const getImage = async () => {
    if (!screenshotRef?.current) {
      logger.debug('Screenshot reference is not available');
      logScreenshotEvent('capture failed - no reference', {
        interview_id: job_interview_id
      });
      return null;
    }
    
    try {
      const screenshot = await takeScreenshot(screenshotRef.current);
      
      // Better validation for screenshot data
      if (!screenshot) {
        logger.debug('Screenshot is empty or null');
        logScreenshotEvent('capture failed - empty data', {
          interview_id: job_interview_id
        });
        return null;
      }
      
      if (typeof screenshot !== 'string') {
        logger.debug('Screenshot is not in expected string format');
        logScreenshotEvent('capture failed - invalid format', {
          interview_id: job_interview_id,
          dataType: typeof screenshot
        });
        return null;
      }
      
      if (!screenshot.startsWith('data:image/png;base64,')) {
        logger.debug('Screenshot capture failed - invalid image data format');
        logScreenshotEvent('capture failed - invalid image format', {
          interview_id: job_interview_id
        });
        return null;
      }
      
      // Make sure the data has a minimum valid length
      if (screenshot.length < 100) {
        logger.debug('Screenshot data is too short to be valid');
        logScreenshotEvent('capture failed - data too short', {
          interview_id: job_interview_id,
          dataLength: screenshot.length
        });
        return null;
      }
      
      logScreenshotEvent('capture successful', {
        interview_id: job_interview_id,
        dataLength: screenshot.length
      });
      return screenshot;
    } catch (error) {
      logger.error('Error taking screenshot:', error);
      logScreenshotError(error, {
        tags: { action: 'take_screenshot' },
        extra: { interview_id: job_interview_id }
      });
      return null;
    }
  };

  // Function to capture and store screenshot with timing info
  const captureScreenshot = async () => {
    const screenshotData = await getImage();
    if (screenshotData && interviewStartTime) {
      const currentTime = new Date();
      
      // Check if enough time has passed since last screenshot (minimum 5 seconds)
      if (lastScreenshotTimeRef.current && 
          (currentTime - lastScreenshotTimeRef.current) < 5000) {
        logger.debug('Skipping screenshot - too soon after last capture');
        return;
      }

      const elapsedSeconds = Math.floor((currentTime - interviewStartTime) / 1000);
      const minutes = Math.floor(elapsedSeconds / 60);
      const seconds = elapsedSeconds % 60;
      const minutes_elapsed = `${minutes}:${seconds < 10 ? `0${seconds}` : seconds}`;

      // Check for duplicate elapsed time
      const isDuplicate = screenshotBufferRef.current.some(
        screenshot => screenshot.minutes_elapsed === minutes_elapsed
      );

      if (isDuplicate) {
        logger.debug('Skipping duplicate screenshot for elapsed time:', minutes_elapsed);
        return;
      }

      const newScreenshot = {
        image_data: screenshotData,
        minutes_elapsed: minutes_elapsed,
        timestamp: currentTime.toISOString()
      };

      screenshotBufferRef.current.push(newScreenshot);
      lastScreenshotTimeRef.current = currentTime;
      
      // Store the last screenshot in localStorage
      localStorage.setItem(`last_screenshot_${job_interview_id}`, JSON.stringify(newScreenshot));
      
      logger.debug('Screenshot captured with elapsed time:', minutes_elapsed);
      logScreenshotEvent('captured and stored', {
        interview_id: job_interview_id,
        minutes_elapsed,
        bufferSize: screenshotBufferRef.current.length
      });
    }
  };

  // Function to send screenshots to server
  const sendScreenshotsToServer = async () => {
    if (isSendingRef.current || screenshotBufferRef.current.length === 0) {
      return;
    }

    try {
      isSendingRef.current = true;
      const screenshots = [...screenshotBufferRef.current];
      
      logScreenshotEvent('batch upload started', {
        interview_id: job_interview_id,
        batchSize: screenshots.length
      });
      
      // Sort screenshots by timestamp to ensure proper sequence
      screenshots.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));

      // Format screenshots according to the data model
      const batchData = {
        interview_id: job_interview_id,
        screenshots: screenshots.map(screenshot => ({
          image_data: screenshot.image_data,
          timestamp: screenshot.timestamp,
          minutes_elapsed: screenshot.minutes_elapsed
        }))
      };

      const result = await fetch(`https://${BASE_URL}/api/screenshots/batch`, {
        method: 'POST',
       // credentials: "include",
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(batchData),
      });

      if (!result.ok) {
        throw new Error(`Screenshot batch upload failed: ${result.status}`);
      }

      logger.debug(`Successfully sent batch of ${screenshots.length} screenshots`);
      logScreenshotEvent('batch upload successful', {
        interview_id: job_interview_id,
        batchSize: screenshots.length,
        responseStatus: result.status
      });
      
      // Clear sent screenshots from buffer
      screenshotBufferRef.current = screenshotBufferRef.current.slice(screenshots.length);
      
      // Clear localStorage after successful send
      localStorage.removeItem(`last_screenshot_${job_interview_id}`);
    } catch (error) {
      logger.error('Error sending screenshots to server:', error);
      logScreenshotError(error, {
        tags: { action: 'batch_upload' },
        extra: { 
          interview_id: job_interview_id,
          batchSize: screenshotBufferRef.current.length
        }
      });
      // Keep the screenshots in buffer if send fails
    } finally {
      isSendingRef.current = false;
    }
  };

  // Set up periodic screenshot capture and upload
  useEffect(() => {
    if (!screenshotRef?.current || !interviewStartTime) return;

    // Take screenshots every 10 seconds
    const captureInterval = setInterval(async () => {
      await captureScreenshot();
    }, 10000);

    // Send screenshots in batches every 90 seconds
    const sendInterval = setInterval(async () => {
      await sendScreenshotsToServer();
    }, 90000);

    // Cleanup on unmount
    return () => {
      clearInterval(captureInterval);
      clearInterval(sendInterval);
      // Send any remaining screenshots before unmounting
      if (screenshotBufferRef.current.length > 0) {
        sendScreenshotsToServer();
      }
    };
  }, [screenshotRef?.current, interviewStartTime, BASE_URL, job_interview_id]);

  return {
    captureScreenshot,
    sendScreenshotsToServer
  };
};

export default useScreenshotCapture;